import {
  CareerSiteFiltersOption as BaseCareerSiteFiltersOption,
  CareerSiteSettings,
} from 'api-clients/monolith';
import { AppContext as AppContextV2 } from 'contexts/v2/app-context';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useFilterActions } from 'hooks/v2/useFilterActions';
import { useFilterConfig } from 'hooks/v2/useFilterConfig';

import { CheckboxSection, INITIAL_VISIBLE_COUNT } from './checkbox-section';
import { DropdownSection } from './dropdown-section';
import {
  FilterConfig,
  FilterKey,
} from './dropdown-section/filter-dropdown/types';

interface CareerSiteFiltersOption extends BaseCareerSiteFiltersOption {
  count?: number;
}

export interface FiltersContentProps {
  brandSetting: CareerSiteSettings;
  filterOptions?: {
    jobTypes: CareerSiteFiltersOption[];
    jobHours: CareerSiteFiltersOption[];
    countries: CareerSiteFiltersOption[];
    states: CareerSiteFiltersOption[];
    cities: CareerSiteFiltersOption[];
    category: CareerSiteFiltersOption[];
    position: CareerSiteFiltersOption[];
    locationName: CareerSiteFiltersOption[];
  };
  usePendingFilters?: boolean;
  // For mobile: persist "Show more" expanded state across modal open/close
  expandedCounts?: Record<string, number>;
  onExpandedCountsChange?: (counts: Record<string, number>) => void;
}

export function FiltersContentSkeleton() {
  return (
    <div data-testid="filters-content-skeleton">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="flex flex-col">
          <div className="flex w-full items-center justify-between p-4">
            <div className="h-5 w-24 animate-pulse rounded bg-gray-200" />
            <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="bg-white p-4">
            <div className="flex flex-col gap-2">
              {[1, 2, 3].map(j => (
                <div key={j} className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function FiltersContent({
  brandSetting,
  filterOptions,
  usePendingFilters = false,
  expandedCounts,
  onExpandedCountsChange,
}: FiltersContentProps) {
  const intl = useIntl();
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const {
    activeFiltersV2,
    setActiveFiltersV2,
    pendingFiltersV2,
    setPendingFiltersV2,
    setPage,
    accountSlug,
    brandId,
    updateUrlWithParams,
    filterCounts,
  } = useContext(AppContextV2);

  const currentFilters = usePendingFilters ? pendingFiltersV2 : activeFiltersV2;
  const setCurrentFilters = usePendingFilters
    ? setPendingFiltersV2
    : setActiveFiltersV2;

  const noOpUpdateUrl = () => {};

  const filterMeta = useFilterConfig({
    brandSetting,
    filterOptions,
    filterCounts: filterCounts ?? undefined,
    intl,
  });

  const {
    handleFilterChange,
    isOptionChecked,
    shouldRenderAsDropdown,
    getActiveFilterKey,
    shouldExpandSection,
  } = useFilterActions({
    activeFiltersV2: currentFilters,
    setActiveFiltersV2: setCurrentFilters,
    setPage,
    accountSlug,
    brandId,
    updateUrlWithParams: usePendingFilters
      ? noOpUpdateUrl
      : updateUrlWithParams,
  });

  const expandedSectionsRef = useRef(expandedSections);
  expandedSectionsRef.current = expandedSections;

  useEffect(() => {
    const sectionsToExpand: Record<string, boolean> = {};

    (Object.keys(filterMeta) as FilterKey[]).forEach(key => {
      if (shouldRenderAsDropdown(key)) return;
      if (expandedSectionsRef.current[key] !== undefined) return;

      const activeKey = getActiveFilterKey(key);
      const value = currentFilters[activeKey];
      const hasSelections = Array.isArray(value) && value.length > 0;

      if (hasSelections) {
        sectionsToExpand[key] = true;
      }
    });

    if (Object.keys(sectionsToExpand).length > 0) {
      setExpandedSections(prev => ({ ...prev, ...sectionsToExpand }));
    }
  }, [currentFilters, filterMeta, getActiveFilterKey, shouldRenderAsDropdown]);

  if (!filterCounts) {
    return <FiltersContentSkeleton />;
  }

  return (
    <div>
      {(Object.entries(filterMeta) as [FilterKey, FilterConfig][]).map(
        ([key, filter]) =>
          filter.showFilter && (
            <div key={key} className="flex flex-col">
              {shouldRenderAsDropdown(key) ? (
                <DropdownSection
                  filterKey={key}
                  filter={filter}
                  activeFiltersV2={currentFilters}
                  getActiveFilterKey={getActiveFilterKey}
                  setActiveFiltersV2={setCurrentFilters}
                  accountSlug={accountSlug ?? null}
                  brandId={brandId ?? ''}
                  updateUrlWithParams={
                    usePendingFilters ? noOpUpdateUrl : updateUrlWithParams
                  }
                  setPage={setPage}
                />
              ) : (
                <CheckboxSection
                  filterKey={key}
                  filter={filter}
                  activeFiltersV2={currentFilters}
                  getActiveFilterKey={getActiveFilterKey}
                  handleFilterChange={handleFilterChange}
                  isOptionChecked={isOptionChecked}
                  shouldExpandSection={filterKey =>
                    shouldExpandSection(
                      filterKey,
                      expandedSections,
                      currentFilters,
                    )
                  }
                  setExpandedSections={setExpandedSections}
                  intl={intl}
                  limitOptions={usePendingFilters}
                  controlledVisibleCount={
                    expandedCounts
                      ? (expandedCounts[key] ?? INITIAL_VISIBLE_COUNT)
                      : undefined
                  }
                  onVisibleCountChange={
                    onExpandedCountsChange
                      ? count =>
                          onExpandedCountsChange({
                            ...expandedCounts,
                            [key]: count,
                          })
                      : undefined
                  }
                />
              )}
            </div>
          ),
      )}
    </div>
  );
}
